
import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap"
import { connect } from "react-redux"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Link } from "react-router-dom"

//Import Actions
import {
    setBreadcrumbItems,
    getCustomers,
    addNewCustomer,
    updateCustomerAction,
    deleteCustomerAction,
    exportCustomersAction,
    getCustomerCategories,
} from "../../store/actions"

const CustomersList = props => {
    document.title = "Annuaire Clients | CRM Entreprise"

    const [modal, setModal] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [currentCustomer, setCurrentCustomer] = useState(null)

    const breadcrumbItems = [
        { title: "CRM", link: "#" },
        { title: "Clients", link: "#" },
        { title: "Annuaire", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Annuaire Clients", breadcrumbItems)
        props.getCustomers()
        props.getCustomerCategories()
    }, [])

    const toggle = () => {
        if (modal) {
            setModal(false)
            setCurrentCustomer(null)
            setIsEdit(false)
        } else {
            setModal(true)
        }
    }

    const handleCustomerClick = arg => {
        const customer = arg
        setCurrentCustomer({
            id: customer.id,
            first_name: customer.first_name,
            last_name: customer.last_name,
            email: customer.email || "",
            phone: customer.phone,
            company_name: customer.company_name || "",
            type: customer.type,
            status: customer.status,
            city: customer.city || "",
            country: customer.country || "Maroc",
            category_id: customer.category_id || "",
        })
        setIsEdit(true)
        toggle()
    }

    const handleDeleteCustomer = customer => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) {
            props.deleteCustomerAction(customer.id)
        }
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            first_name: (currentCustomer && currentCustomer.first_name) || "",
            last_name: (currentCustomer && currentCustomer.last_name) || "",
            email: (currentCustomer && currentCustomer.email) || "",
            phone: (currentCustomer && currentCustomer.phone) || "",
            company_name: (currentCustomer && currentCustomer.company_name) || "",
            type: (currentCustomer && currentCustomer.type) || "individual",
            status: (currentCustomer && currentCustomer.status) || "lead",
            city: (currentCustomer && currentCustomer.city) || "",
            country: (currentCustomer && currentCustomer.country) || "Maroc",
            category_id: (currentCustomer && currentCustomer.category_id) || "",
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required("Prénom requis"),
            last_name: Yup.string().required("Nom requis"),
            phone: Yup.string().required("Téléphone requis"),
            type: Yup.string().required("Type requis"),
            status: Yup.string().required("Statut requis"),
            category_id: Yup.string().nullable(),
        }),
        onSubmit: values => {
            if (isEdit) {
                const updatedCustomer = {
                    id: currentCustomer.id,
                    ...values,
                }
                props.updateCustomerAction(updatedCustomer)
            } else {
                props.addNewCustomer(values)
            }
            toggle()
        },
    })

    const statusColors = {
        lead: "info",
        prospect: "warning",
        customer: "success",
        inactive: "secondary",
        blacklisted: "danger",
    }

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Row>
                    <Col className="col-12">
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <CardTitle className="h4 mb-0">Annuaire des Clients</CardTitle>
                                    <div className="d-flex gap-2">
                                        <Button color="primary" outline onClick={() => props.exportCustomersAction()}>
                                            <i className="mdi mdi-download me-1"></i> Exporter CSV
                                        </Button>
                                        <Button color="success" onClick={toggle}>
                                            <i className="mdi mdi-plus me-1"></i> Nouveau Client
                                        </Button>
                                    </div>
                                </div>
                                <Row className="mb-4">
                                    <Col md="3">
                                        <Input
                                            type="select"
                                            onChange={(e) => props.getCustomers({ category_id: e.target.value })}
                                        >
                                            <option value="">Toutes les catégories</option>
                                            {props.categories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md="3">
                                        <Input
                                            type="select"
                                            onChange={(e) => props.getCustomers({ status: e.target.value })}
                                        >
                                            <option value="">Tous les statuts</option>
                                            <option value="lead">Lead</option>
                                            <option value="prospect">Prospect</option>
                                            <option value="customer">Client Actif</option>
                                            <option value="inactive">Inactif</option>
                                        </Input>
                                    </Col>
                                </Row>

                                <div className="table-responsive">
                                    <table className="table table-centered table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Code</th>
                                                <th>Client</th>
                                                <th>Contact</th>
                                                <th>Type</th>
                                                <th>Catégorie</th>
                                                <th>Ville</th>
                                                <th>Statut</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {props.customers && props.customers.data && props.customers.data.map((customer, key) => (
                                                <tr key={key}>
                                                    <td>
                                                        <span className="text-primary fw-bold">{customer.customer_number}</span>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <h5 className="font-size-14 mb-1">
                                                                {customer.first_name} {customer.last_name}
                                                            </h5>
                                                            <p className="text-muted mb-0">{customer.company_name}</p>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <p className="mb-1">{customer.email}</p>
                                                        <p className="text-muted mb-0">{customer.phone}</p>
                                                    </td>
                                                    <td>
                                                        {customer.type === 'company' ? 'Entreprise' : 'Particulier'}
                                                    </td>
                                                    <td>
                                                        {customer.category ? (
                                                            <Badge color="soft" style={{ backgroundColor: customer.category.color + '22', color: customer.category.color }}>
                                                                {customer.category.name}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>{customer.city}</td>
                                                    <td>
                                                        <Badge color={statusColors[customer.status] || "primary"}>
                                                            {customer.status.toUpperCase()}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Link
                                                                to={`/clients/${customer.id}`}
                                                                className="btn btn-info btn-sm"
                                                            >
                                                                <i className="mdi mdi-eye"></i>
                                                            </Link>
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleCustomerClick(customer)}
                                                            >
                                                                <i className="mdi mdi-pencil"></i>
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => handleDeleteCustomer(customer)}
                                                            >
                                                                <i className="mdi mdi-trash-can"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <Modal isOpen={modal} toggle={toggle} centered size="lg">
                <ModalHeader toggle={toggle} tag="h4">
                    {!!isEdit ? "Modifier le Client" : "Nouveau Client"}
                </ModalHeader>
                <ModalBody>
                    <Form
                        onSubmit={e => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                        }}
                    >
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Type de Client</Label>
                                    <Input
                                        name="type"
                                        type="select"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.type || ""}
                                    >
                                        <option value="individual">Particulier</option>
                                        <option value="company">Entreprise</option>
                                    </Input>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Statut</Label>
                                    <Input
                                        name="status"
                                        type="select"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.status || ""}
                                    >
                                        <option value="lead">Lead</option>
                                        <option value="prospect">Prospect</option>
                                        <option value="customer">Client Actif</option>
                                        <option value="inactive">Inactif</option>
                                    </Input>
                                </div>
                            </Col>
                        </Row>
                        <div className="mb-3">
                            <Label>Catégorie</Label>
                            <Input
                                name="category_id"
                                type="select"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.category_id || ""}
                            >
                                <option value="">Choisir une catégorie...</option>
                                {props.categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Input>
                        </div>
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Prénom</Label>
                                    <Input
                                        name="first_name"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.first_name || ""}
                                        invalid={validation.touched.first_name && validation.errors.first_name ? true : false}
                                    />
                                    {validation.touched.first_name && validation.errors.first_name ? (
                                        <FormFeedback type="invalid">{validation.errors.first_name}</FormFeedback>
                                    ) : null}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Nom</Label>
                                    <Input
                                        name="last_name"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.last_name || ""}
                                        invalid={validation.touched.last_name && validation.errors.last_name ? true : false}
                                    />
                                    {validation.touched.last_name && validation.errors.last_name ? (
                                        <FormFeedback type="invalid">{validation.errors.last_name}</FormFeedback>
                                    ) : null}
                                </div>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <Label>Nom de l'Entreprise (Si applicable)</Label>
                            <Input
                                name="company_name"
                                type="text"
                                onChange={validation.handleChange}
                                value={validation.values.company_name || ""}
                            />
                        </div>

                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Email</Label>
                                    <Input
                                        name="email"
                                        type="email"
                                        onChange={validation.handleChange}
                                        value={validation.values.email || ""}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Téléphone</Label>
                                    <Input
                                        name="phone"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.phone || ""}
                                        invalid={validation.touched.phone && validation.errors.phone ? true : false}
                                    />
                                    {validation.touched.phone && validation.errors.phone ? (
                                        <FormFeedback type="invalid">{validation.errors.phone}</FormFeedback>
                                    ) : null}
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Ville</Label>
                                    <Input
                                        name="city"
                                        type="text"
                                        onChange={validation.handleChange}
                                        value={validation.values.city || ""}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Pays</Label>
                                    <Input
                                        name="country"
                                        type="text"
                                        onChange={validation.handleChange}
                                        value={validation.values.country || ""}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div className="text-end mt-4">
                            <Button type="button" color="secondary" className="me-2" onClick={toggle}>Annuler</Button>
                            <Button type="submit" color="success">
                                {isEdit ? "Mettre à jour" : "Créer le Client"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    customers: state.Customers.customers,
    categories: state.Customers.categories,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getCustomers,
    addNewCustomer,
    updateCustomerAction,
    deleteCustomerAction,
    exportCustomersAction,
    getCustomerCategories,
})(CustomersList)
